import { Box, Button, Divider, HStack, Image, Text, useDisclosure, VStack } from "@chakra-ui/react";
import { FiImage, FiPhone } from "react-icons/fi";
import EmailPreviewModal from "./EmailPreviewModal";

/**
 * Is this email body HTML that must be rendered rather than printed as text?
 *
 * The old check was `emailType === 'html'`, but nothing ever sets that value —
 * the real ones are 'text', 'html_code' and 'email_template'. So the branch was
 * dead and every HTML email fell through to the plain-text renderer, which is why
 * the campaign screens showed "<!DOCTYPE html> <html xmlns=..." as visible source.
 *
 * Also sniffs the body, so a template whose type is missing (older records, or a
 * campaign payload that never carried emailType) is still detected.
 */
export const isHtmlBody = (body, emailType) => {
    if (emailType === 'html' || emailType === 'html_code' || emailType === 'email_template') return true;
    return /<\/?(!doctype|html|body|table|tr|td|div|p|br|span|img|a|h[1-6]|style)\b/i.test(String(body || ''));
};

export const parseWhatsAppMarkdown = (text) => {
    if (!text) return null;
    const regex = /```([\s\S]+?)```|\*([^*]+?)\*|~([^~]+?)~|_([^_]+?)_/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let key = 0;
    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }
        if (match[1] !== undefined) {
            parts.push(<code key={key++} style={{ fontFamily: 'monospace', background: '#f0f0f0', padding: '0 2px', borderRadius: '2px', fontSize: '0.9em' }}>{match[1]}</code>);
        } else if (match[2] !== undefined) {
            parts.push(<strong key={key++}>{match[2]}</strong>);
        } else if (match[3] !== undefined) {
            parts.push(<span key={key++} style={{ textDecoration: 'line-through' }}>{match[3]}</span>);
        } else if (match[4] !== undefined) {
            parts.push(<em key={key++}>{match[4]}</em>);
        }
        lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }
    return parts.length > 0 ? parts : text;
};

// Preview Components
export const WhatsAppPreview = ({ header, body, footer, buttons, otpType, authAddExpirationTime, authCodeExpiryMinutes, templateType }) => (
    <Box p={4} borderWidth="1px" borderRadius="lg" bg="#e5ddd5" maxW="300px">

        <VStack align="stretch" spacing={3} bg={"white"} p={4} borderRadius={"lg"}>
            {header?.type === 'text' && header?.content && (
                <Text fontWeight="bold" fontSize="sm">{header.content}</Text>
            )}
            {header?.type === 'image' && header?.file && (
                <Box borderRadius="md" overflow="hidden">
                    <img src={header.file} alt="Header" style={{ width: '100%', height: 'auto' }} />
                </Box>
            )}
            {header?.type === 'video' && header?.file && (
                <Box borderRadius="md" overflow="hidden" bg="gray.800" p={4} textAlign="center">

                    {header.file ? <video controls>

                        <source src={header.file} />
                        Your browser does not support the video tag.
                    </video> : <>
                        <FiImage size={32} color="white" style={{ margin: '0 auto' }} />
                        <Text fontSize="xs" color="white" mt={2}>Video: {header.file}</Text>
                    </>}

                    {/* <video src={header.file}  /> */}

                </Box>
            )}
            {header?.type === 'document' && header?.file && (
                <Box borderWidth="1px" borderRadius="md" p={2} bg="white">
                    <Text fontSize="xs" fontWeight="bold">Document</Text>
                    <Text fontSize="xs" color="gray.600" noOfLines={1}>{header.file}</Text>
                </Box>
            )}
            <Text fontSize="sm" whiteSpace="pre-wrap">{parseWhatsAppMarkdown(body) || 'Message body will appear here'}</Text>
            {(authAddExpirationTime && templateType === '1') && (
                <Text fontSize="xs" whiteSpace="pre-wrap" color={"grey"}>Expires in {authCodeExpiryMinutes} {authCodeExpiryMinutes > 1 ? 'minutes' : 'minute'}.</Text>
            )}
            {footer && <Text fontSize="xs" color="gray.600">{footer}</Text>}
            {buttons?.length > 0 && (
                <VStack spacing={1}>
                    {buttons.map((btn, idx) => (
                        <Button key={idx} size="sm" variant="outline" w="full" fontSize="xs">
                            {btn.type === 'copy-offer' ? 'Copy offer code' : (btn.name || 'Button')}
                        </Button>
                    ))}
                </VStack>
            )}
            {(otpType && templateType === '1') && (
                otpType === 'COPY_CODE' ? <> <Divider /> <Text align={"center"} color={"var(--brand-primary)"} cursor={"pointer"} fontSize="xs">
                    {otpType === 'COPY_CODE' ? 'Copy Code' : "Auto fill"}
                </Text> </> : otpType === 'ONE_TAP' ? <><Divider /><Text align={"center"} color={"var(--brand-primary)"} cursor={"pointer"} fontSize="xs">
                    {otpType === 'ONE_TAP' ? 'Auto Fill' : ""}
                </Text>
                </> : ""

            )}
        </VStack>
    </Box>
);

export const InAppPreview = ({ header, body, footer, buttons, otpType, isWhatsapp }) => (
    <Box p={4} borderRadius="lg" bg="#f6f3f4" maxW="300px">
        <VStack align="stretch" spacing={3} p={4} borderRadius={"lg"} bg={"white"} boxShadow={"md"}>
            {header?.type === 'text' && header?.content && (
                <Text fontWeight="bold" fontSize="sm">{header.content}</Text>
            )}
            {header?.type === 'image' && header?.file && (

                <Box borderRadius="md" overflow="hidden">
                    <img src={header.file} alt="Header" style={{ width: '100%', height: 'auto' }} />
                </Box>
            )}
            {header?.type === 'video' && header?.file && (
                // <Box borderRadius="md" overflow="hidden" bg="gray.800" p={4} textAlign="center">
                //     <FiImage size={32} color="white" style={{ margin: '0 auto' }} />
                //     <Text fontSize="xs" color="white" mt={2}>Video: {header.file}</Text>
                // </Box>
                <Box borderRadius="md" overflow="hidden" bg="gray.800" p={4} textAlign="center">

                    {header.file ? <video controls>

                        <source src={header.file} />
                        Your browser does not support the video tag.
                    </video> : <>
                        <FiImage size={32} color="white" style={{ margin: '0 auto' }} />
                        <Text fontSize="xs" color="white" mt={2}>Video: {header.file}</Text>
                    </>}

                    {/* <video src={header.file}  /> */}

                </Box>
            )}
            {header?.type === 'document' && header?.file && (
                <Box borderWidth="1px" borderRadius="md" p={2} bg="white">
                    <Text fontSize="xs" fontWeight="bold">Document</Text>
                    <Text fontSize="xs" color="gray.600" noOfLines={1}>{header.file}</Text>
                </Box>
            )}
            <Text fontSize="sm" whiteSpace="pre-wrap">{body || 'Message body will appear here'}</Text>
            {footer && <Text fontSize="xs" color="gray.600">{footer}</Text>}
            {buttons?.length > 0 && (
                <VStack spacing={1}>
                    {buttons.map((btn, idx) => (
                        <Button key={idx} size="sm" variant="outline" w="full" fontSize="xs">
                            {btn.type === 'copy-offer' ? 'Copy offer code' : (btn.name || 'Button')}
                        </Button>
                    ))}
                </VStack>
            )}
            {(otpType && isWhatsapp) && (
                otpType === 'COPY_CODE' ? <> <Divider /> <Text align={"center"} color={"var(--brand-primary)"} cursor={"pointer"} fontSize="xs">
                    {otpType === 'COPY_CODE' ? 'Copy Code' : ""}
                </Text> </> : <></>

            )}
        </VStack>
    </Box>
);

export const SMSPreview = ({ body = '' }) => {
    const chunkSize = 160;

    const chunks = body
        ? body.match(new RegExp(`.{1,${chunkSize}}`, 'g'))
        : ['SMS message will appear here'];


    return (
        <Box>
            <VStack
                p={4}
                borderWidth="1px"
                borderRadius="lg"
                bg="blue.50"
                maxW="300px"
                mb={3}
                gap={3}
                align="stretch"
            >
                <Box

                    bg="white"
                    p={3}
                    borderRadius="md"
                    boxShadow="sm"
                >
                    <Text fontSize="xs" color="gray.600" mb={2}>
                        SMS Message
                    </Text>

                    <Text fontSize="sm" whiteSpace="pre-wrap">
                        {body || 'SMS message will appear here'}
                    </Text>
                </Box>
            </VStack>
        </Box>
    );
};



export const PushPreview = ({ title, body, imageUrl }) => (
    <Box p={4} borderWidth="1px" borderRadius="lg" bg="orange.50" maxW="300px">
        <Box bg="white" p={3} borderRadius="md" boxShadow="md">
            <HStack spacing={2} mb={2} alignItems="flex-start">
                {imageUrl ? (
                    <Box
                        w={8}
                        h={8}
                        bg="#eeeff0ff"
                        borderRadius="md"
                        flexShrink={0}
                        p={1}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Image
                            src={imageUrl}
                            maxW="100%"
                            maxH="100%"
                            objectFit="contain"
                        />
                    </Box>
                ) : (
                    <Box
                        w={8}
                        h={8}
                        bg="#eeeff0ff"
                        borderRadius="md"
                        flexShrink={0}
                    />
                )}


                <Box flex={1}>
                    <Text fontWeight="bold" fontSize="sm" noOfLines={1}>
                        {title || 'Notification Title'}
                    </Text>
                    <Text fontSize="xs" color="gray.600" noOfLines={2}>
                        {body || 'Notification body'}
                    </Text>
                </Box>
            </HStack>
            {/* {imageUrl && (
                <Box mt={2} borderRadius="md" overflow="hidden">
                    <img src={imageUrl} alt="Notification" style={{ width: '100%', height: 'auto' }} />
                </Box>
            )} */}
        </Box>
    </Box>
);

export const EmailPreview = ({ subject, body, emailType }) => {
    const { isOpen: isEmailPreviewOpen, onOpen: onEmailPreviewOpen, onClose: onEmailPreviewClose } = useDisclosure();
    return (
        <Box p={4} borderWidth="1px" borderRadius="lg" bg="red.50" maxW="300px">
            <Box bg="white" p={3} borderRadius="md" boxShadow="sm">
                <Text fontSize={"sm"} fontWeight={"600"}>Subject:</Text>
                <Text fontSize="sm" mb={3}>
                    {subject || 'Email Subject'}
                </Text>
                <Text fontSize={"sm"} fontWeight={"600"}>Body:</Text>
                {isHtmlBody(body, emailType) ? (
                    <Text
                        as="button" type="button" textAlign="left"
                        fontSize="xs" cursor="pointer" onClick={onEmailPreviewOpen}
                        color="var(--brand-primary)" fontWeight="600"
                        textDecoration="underline"
                        _hover={{ color: 'var(--brand-primary-hover)' }}
                    >
                        Preview Email
                    </Text>
                ) : <Text fontSize="xs" noOfLines={4}>
                    {body || 'Email body content'}
                </Text>}

            </Box>
            <EmailPreviewModal subject={subject} body={body} onEmailPreviewClose={onEmailPreviewClose} isEmailPreviewOpen={isEmailPreviewOpen} />
        </Box>
    )
};

export const VoicePreview = ({ script }) => (
    <Box p={4} borderWidth="1px" borderRadius="lg" bg="purple.50" maxW="300px">
        <VStack align="stretch" spacing={3}>
            <HStack spacing={2}>
                <FiPhone />
                <Text fontWeight="bold" fontSize="sm">Voice Call</Text>
            </HStack>
            <Box bg="white" p={3} borderRadius="md">
                <Text fontSize="xs" whiteSpace="pre-wrap">
                    {script || 'Voice script will appear here'}
                </Text>
            </Box>
        </VStack>
    </Box>
);